import { AgentMessageData, AgentMessageResponse, StreamingMessage } from '@/models/agentMessage';
import { AgentMessageType, WebsocketMessageType } from '@/models/enums';

/**
 * Interface for safe agent message data with validation metadata
 */
export interface SafeAgentMessageData extends AgentMessageData {
  // Validation metadata
  _isValidated?: boolean;
  _validationErrors?: string[];
  _fallbacksUsed?: string[];
  
  // Safe accessors with fallbacks
  safeAgent: string;
  safeAgentType: AgentMessageType;
  safeContent: string;
  safeTimestamp: number;
}

/**
 * Interface for normalizing different agent data structures
 */
export interface AgentDataNormalizer {
  normalizeAgentMessage(data: any): SafeAgentMessageData;
  normalizeTeamAgent(agent: any): SafeAgent;
  validateAgentType(type: any): AgentMessageType;
}

/**
 * Interface for safe agent data
 */
export interface SafeAgent {
  name: string;
  type: string;
  input_key: string;
  icon?: string;
  description?: string;
  system_message?: string;
  index_name?: string;
}

/**
 * Error context structure for logging
 */
export interface ErrorContext {
  component: string;
  function: string;
  agentName?: string;
  agentType?: string;
  originalData?: any;
  timestamp: number;
  userAgent: string;
  url: string;
}

/**
 * Validation schema for agent messages
 */
const AgentMessageSchema = {
  required: {
    agent: 'string',
    agent_type: 'AgentMessageType',
    timestamp: 'number'
  },
  optional: {
    content: 'string',
    steps: 'array',
    next_steps: 'array',
    raw_data: 'string'
  },
  fallbacks: {
    agent: 'Assistant',
    agent_type: AgentMessageType.AI_AGENT,
    content: '',
    timestamp: () => Date.now(),
    steps: [],
    next_steps: [],
    raw_data: ''
  }
};

/**
 * TypeScript type guards for runtime validation
 */
export class TypeGuards {
  /**
   * Type guard to check if value is a string
   */
  static isString(value: any): value is string {
    return typeof value === 'string';
  }

  /**
   * Type guard to check if value is a number
   */
  static isNumber(value: any): value is number {
    return typeof value === 'number' && !isNaN(value);
  }

  /**
   * Type guard to check if value is an array
   */
  static isArray(value: any): value is any[] {
    return Array.isArray(value);
  }

  /**
   * Type guard to check if value is a valid AgentMessageType
   */
  static isAgentMessageType(value: any): value is AgentMessageType {
    return Object.values(AgentMessageType).includes(value);
  }

  /**
   * Type guard to check if value is a valid WebsocketMessageType
   */
  static isWebsocketMessageType(value: any): value is WebsocketMessageType {
    return Object.values(WebsocketMessageType).includes(value);
  }

  /**
   * Type guard to check if object has required agent message properties
   */
  static isAgentMessageData(value: any): value is AgentMessageData {
    if (!value || typeof value !== 'object') {
      return false;
    }

    // Check for required fields with flexible property names
    const hasAgent = this.isString(value.agent) || this.isString(value.name);
    const hasAgentType = this.isAgentMessageType(value.agent_type) || this.isAgentMessageType(value.type);
    const hasTimestamp = this.isNumber(value.timestamp);

    return hasAgent && hasAgentType && hasTimestamp;
  }

  /**
   * Type guard to check if object is a valid agent configuration
   */
  static isValidAgent(value: any): value is SafeAgent {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return this.isString(value.name) && 
           this.isString(value.type) && 
           this.isString(value.input_key);
  }

  /**
   * Type guard to check if object is a streaming message
   */
  static isStreamingMessage(value: any): value is StreamingMessage {
    if (!value || typeof value !== 'object') {
      return false;
    }

    return this.isWebsocketMessageType(value.type) &&
           this.isString(value.agent) &&
           this.isString(value.content) &&
           typeof value.is_final === 'boolean';
  }


}

/**
 * Data sanitization and normalization functions
 */
export class DataSanitizer {
  /**
   * Sanitize HTML content to prevent XSS
   */
  static sanitizeHtml(content: string): string {
    if (!TypeGuards.isString(content)) {
      return '';
    }

    // Basic HTML sanitization - remove script tags and dangerous attributes
    return content
      .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
      .replace(/javascript:/gi, '')
      .replace(/on\w+\s*=/gi, '')
      .replace(/<iframe\b[^<]*(?:(?!<\/iframe>)<[^<]*)*<\/iframe>/gi, '')
      .replace(/<object\b[^<]*(?:(?!<\/object>)<[^<]*)*<\/object>/gi, '')
      .replace(/<embed\b[^<]*(?:(?!<\/embed>)<[^<]*)*<\/embed>/gi, '');
  }

  /**
   * Sanitize and normalize agent name
   */
  static sanitizeAgentName(name: any): string {
    if (!TypeGuards.isString(name) || name.trim() === '') {
      return AgentMessageSchema.fallbacks.agent;
    }

    // Remove HTML tags and potentially dangerous characters
    return name
      .replace(/<[^>]*>/g, '') // Remove HTML tags
      .replace(/[\"'&]/g, '') // Remove quotes and ampersands
      .replace(/\s+/g, ' ') // Normalize whitespace
      .trim()
      .substring(0, 100); // Limit length
  }

  /**
   * Sanitize and normalize agent type
   */
  static sanitizeAgentType(type: any): AgentMessageType {
    if (TypeGuards.isAgentMessageType(type)) {
      return type;
    }

    // Try to normalize string values to valid AgentMessageType
    if (TypeGuards.isString(type)) {
      const normalizedType = type.toUpperCase().replace(/\s+/g, '_');
      if (normalizedType.includes('HUMAN')) {
        return AgentMessageType.HUMAN_AGENT;
      }
      if (normalizedType.includes('AI') || normalizedType.includes('BOT')) {
        return AgentMessageType.AI_AGENT;
      }
    }

    return AgentMessageSchema.fallbacks.agent_type;
  }

  /**
   * Sanitize content string
   */
  static sanitizeContent(content: any): string {
    if (!TypeGuards.isString(content)) {
      return AgentMessageSchema.fallbacks.content;
    }

    return this.sanitizeHtml(content).substring(0, 10000); // Limit content length
  }

  /**
   * Sanitize timestamp
   */
  static sanitizeTimestamp(timestamp: any): number {
    if (TypeGuards.isNumber(timestamp) && timestamp > 0) {
      return timestamp;
    }

    // Try to parse string timestamps
    if (TypeGuards.isString(timestamp)) {
      const parsed = parseInt(timestamp, 10);
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    }

    return AgentMessageSchema.fallbacks.timestamp();
  }

  /**
   * Sanitize array data
   */
  static sanitizeArray(arr: any): any[] {
    if (TypeGuards.isArray(arr)) {
      return arr.slice(0, 100); // Limit array size
    }
    return [];
  }
}

/**
 * Main validator class for agent messages
 */
export class AgentMessageValidator implements AgentDataNormalizer {
  private static instance: AgentMessageValidator;

  /**
   * Get singleton instance
   */
  static getInstance(): AgentMessageValidator {
    if (!AgentMessageValidator.instance) {
      AgentMessageValidator.instance = new AgentMessageValidator();
    }
    return AgentMessageValidator.instance;
  }

  /**
   * Validate required fields in agent message data
   */
  validateRequiredFields(data: any): { isValid: boolean; errors: string[]; fallbacksUsed: string[] } {
    const errors: string[] = [];
    const fallbacksUsed: string[] = [];

    if (!data || typeof data !== 'object') {
      errors.push('Agent message data must be a valid object');
      return { isValid: false, errors, fallbacksUsed };
    }

    // Check agent field (flexible - could be 'agent' or 'name')
    const agentValue = data.agent || data.name;
    if (!TypeGuards.isString(agentValue) || agentValue.trim() === '') {
      errors.push('Missing or invalid agent name');
      fallbacksUsed.push('agent');
    }

    // Check agent_type field (flexible - could be 'agent_type' or 'type')
    const agentTypeValue = data.agent_type || data.type;
    if (!TypeGuards.isAgentMessageType(agentTypeValue)) {
      errors.push('Missing or invalid agent_type');
      fallbacksUsed.push('agent_type');
    }

    // Check timestamp
    if (!TypeGuards.isNumber(data.timestamp)) {
      errors.push('Missing or invalid timestamp');
      fallbacksUsed.push('timestamp');
    }

    return { 
      isValid: errors.length === 0, 
      errors, 
      fallbacksUsed 
    };
  }

  /**
   * Normalize agent message data with fallbacks
   */
  normalizeAgentMessage(data: any): SafeAgentMessageData {
    const validation = this.validateRequiredFields(data);
    
    // Extract values with fallbacks
    const agent = DataSanitizer.sanitizeAgentName(data?.agent || data?.name);
    const agent_type = DataSanitizer.sanitizeAgentType(data?.agent_type || data?.type);
    const content = DataSanitizer.sanitizeContent(data?.content);
    const timestamp = DataSanitizer.sanitizeTimestamp(data?.timestamp);
    const steps = DataSanitizer.sanitizeArray(data?.steps);
    const next_steps = DataSanitizer.sanitizeArray(data?.next_steps);
    const raw_data = DataSanitizer.sanitizeContent(data?.raw_data);

    const normalizedData: SafeAgentMessageData = {
      agent,
      agent_type,
      content,
      timestamp,
      steps,
      next_steps,
      raw_data,
      
      // Validation metadata
      _isValidated: true,
      _validationErrors: validation.errors,
      _fallbacksUsed: validation.fallbacksUsed,
      
      // Safe accessors
      safeAgent: agent,
      safeAgentType: agent_type,
      safeContent: content,
      safeTimestamp: timestamp
    };

    return normalizedData;
  }

  /**
   * Normalize team agent configuration
   */
  normalizeTeamAgent(agent: any): SafeAgent {
    if (!agent || typeof agent !== 'object') {
      throw new Error('Invalid agent configuration object');
    }

    return {
      name: DataSanitizer.sanitizeAgentName(agent.name),
      type: DataSanitizer.sanitizeAgentName(agent.type),
      input_key: DataSanitizer.sanitizeAgentName(agent.input_key),
      icon: TypeGuards.isString(agent.icon) ? agent.icon.trim() : undefined,
      description: TypeGuards.isString(agent.description) ? DataSanitizer.sanitizeContent(agent.description) : undefined,
      system_message: TypeGuards.isString(agent.system_message) ? DataSanitizer.sanitizeContent(agent.system_message) : undefined,
      index_name: TypeGuards.isString(agent.index_name) ? agent.index_name.trim() : undefined
    };
  }

  /**
   * Validate agent type with normalization
   */
  validateAgentType(type: any): AgentMessageType {
    return DataSanitizer.sanitizeAgentType(type);
  }

  /**
   * Validate WebSocket message before processing
   */
  validateWebSocketMessage(message: any): { isValid: boolean; normalizedMessage?: any; errors: string[] } {
    const errors: string[] = [];

    if (!message || typeof message !== 'object') {
      errors.push('WebSocket message must be a valid object');
      return { isValid: false, errors };
    }

    // Check message type
    if (!TypeGuards.isWebsocketMessageType(message.type)) {
      errors.push('Invalid or missing WebSocket message type');
    }

    // Validate based on message type - only require agent_name for agent messages
    // Content can be empty for progress/status messages
    if (message.type === WebsocketMessageType.AGENT_MESSAGE || 
        message.type === WebsocketMessageType.AGENT_MESSAGE_STREAMING) {
      
      // Check for agent name in the correct location (message.data.agent_name)
      const agentName = message.data?.agent_name || message.data?.agent || message.agent;
      if (!TypeGuards.isString(agentName) || agentName.trim() === '') {
        errors.push('Agent message must have valid agent name');
      }
      
      // Content is optional - don't fail validation if empty
      // Some progress messages may have empty content
    }

    if (errors.length > 0) {
      return { isValid: false, errors };
    }

    // Normalize the message
    const agentName = message.data?.agent_name || message.data?.agent || message.agent;
    const content = message.data?.content || message.content || '';
    
    const normalizedMessage = {
      ...message,
      data: {
        ...message.data,
        agent_name: agentName ? DataSanitizer.sanitizeAgentName(agentName) : undefined,
        content: content ? DataSanitizer.sanitizeContent(content) : '',
        timestamp: message.data?.timestamp ? DataSanitizer.sanitizeTimestamp(message.data.timestamp) : Date.now()
      },
      timestamp: message.timestamp ? message.timestamp : new Date().toISOString()
    };

    return { isValid: true, normalizedMessage, errors: [] };
  }

  /**
   * Safe property access with optional chaining simulation
   */
  safeGet<T>(obj: any, path: string, defaultValue: T): T {
    if (!obj || typeof obj !== 'object') {
      return defaultValue;
    }

    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (current == null || typeof current !== 'object' || !(key in current)) {
        return defaultValue;
      }
      current = current[key];
    }

    return current !== undefined ? current : defaultValue;
  }

  /**
   * Validate data conversion between formats
   */
  validateDataConversion<TInput, TOutput>(
    input: TInput,
    converter: (input: TInput) => TOutput,
    validator: (output: TOutput) => boolean
  ): { isValid: boolean; result?: TOutput; error?: string } {
    try {
      const result = converter(input);
      const isValid = validator(result);
      
      if (!isValid) {
        return { isValid: false, error: 'Conversion result failed validation' };
      }
      
      return { isValid: true, result };
    } catch (error) {
      return { 
        isValid: false, 
        error: error instanceof Error ? error.message : 'Unknown conversion error' 
      };
    }
  }

  /**
   * Create error context for logging
   */
  createErrorContext(
    component: string,
    functionName: string,
    additionalData?: Partial<ErrorContext>
  ): ErrorContext {
    return {
      component,
      function: functionName,
      timestamp: Date.now(),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : 'Unknown',
      url: typeof window !== 'undefined' ? window.location.href : 'Unknown',
      ...additionalData
    };
  }
}

/**
 * Convenience functions for common validation tasks
 */
export const validateAgentMessage = (data: any): SafeAgentMessageData => {
  return AgentMessageValidator.getInstance().normalizeAgentMessage(data);
};

export const validateWebSocketMessage = (message: any) => {
  return AgentMessageValidator.getInstance().validateWebSocketMessage(message);
};

export const safePropertyAccess = <T>(obj: any, path: string, defaultValue: T): T => {
  return AgentMessageValidator.getInstance().safeGet(obj, path, defaultValue);
};

export const createErrorContext = (component: string, functionName: string, additionalData?: Partial<ErrorContext>): ErrorContext => {
  return AgentMessageValidator.getInstance().createErrorContext(component, functionName, additionalData);
};